/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name covarmtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/covarmtk}
*/
import covarmtk from './../../strided/covarmtk';
setReadOnly( ns, 'covarmtk', covarmtk );

/**
* @name dcovarmtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dcovarmtk}
*/
import dcovarmtk from './../../strided/dcovarmtk';
setReadOnly( ns, 'dcovarmtk', dcovarmtk );

/**
* @name dcovmatmtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dcovmatmtk}
*/
import dcovmatmtk from './../../strided/dcovmatmtk';
setReadOnly( ns, 'dcovmatmtk', dcovmatmtk );

/**
* @name dcumax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dcumax}
*/
import dcumax from './../../strided/dcumax';
setReadOnly( ns, 'dcumax', dcumax );

/**
* @name dcumaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dcumaxabs}
*/
import dcumaxabs from './../../strided/dcumaxabs';
setReadOnly( ns, 'dcumaxabs', dcumaxabs );

/**
* @name dcumin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dcumin}
*/
import dcumin from './../../strided/dcumin';
setReadOnly( ns, 'dcumin', dcumin );

/**
* @name dcuminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dcuminabs}
*/
import dcuminabs from './../../strided/dcuminabs';
setReadOnly( ns, 'dcuminabs', dcuminabs );

/**
* @name distances
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/strided/distances}
*/
import distances from './../../strided/distances';
setReadOnly( ns, 'distances', distances );

/**
* @name dmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmax}
*/
import dmax from './../../strided/dmax';
setReadOnly( ns, 'dmax', dmax );

/**
* @name dmaxSorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmax-sorted}
*/
import dmaxSorted from './../../strided/dmax-sorted';
setReadOnly( ns, 'dmaxSorted', dmaxSorted );

/**
* @name dmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmaxabs}
*/
import dmaxabs from './../../strided/dmaxabs';
setReadOnly( ns, 'dmaxabs', dmaxabs );

/**
* @name dmaxabsSorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmaxabs-sorted}
*/
import dmaxabsSorted from './../../strided/dmaxabs-sorted';
setReadOnly( ns, 'dmaxabsSorted', dmaxabsSorted );

/**
* @name dmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmean}
*/
import dmean from './../../strided/dmean';
setReadOnly( ns, 'dmean', dmean );

/**
* @name dmeankbn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmeankbn}
*/
import dmeankbn from './../../strided/dmeankbn';
setReadOnly( ns, 'dmeankbn', dmeankbn );

/**
* @name dmeankbn2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmeankbn2}
*/
import dmeankbn2 from './../../strided/dmeankbn2';
setReadOnly( ns, 'dmeankbn2', dmeankbn2 );

/**
* @name dmeanli
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmeanli}
*/
import dmeanli from './../../strided/dmeanli';
setReadOnly( ns, 'dmeanli', dmeanli );

/**
* @name dmeanlipw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmeanlipw}
*/
import dmeanlipw from './../../strided/dmeanlipw';
setReadOnly( ns, 'dmeanlipw', dmeanlipw );

/**
* @name dmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmeanors}
*/
import dmeanors from './../../strided/dmeanors';
setReadOnly( ns, 'dmeanors', dmeanors );

/**
* @name dmeanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmeanpn}
*/
import dmeanpn from './../../strided/dmeanpn';
setReadOnly( ns, 'dmeanpn', dmeanpn );

/**
* @name dmeanpw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmeanpw}
*/
import dmeanpw from './../../strided/dmeanpw';
setReadOnly( ns, 'dmeanpw', dmeanpw );

/**
* @name dmeanstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmeanstdev}
*/
import dmeanstdev from './../../strided/dmeanstdev';
setReadOnly( ns, 'dmeanstdev', dmeanstdev );

/**
* @name dmeanstdevpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmeanstdevpn}
*/
import dmeanstdevpn from './../../strided/dmeanstdevpn';
setReadOnly( ns, 'dmeanstdevpn', dmeanstdevpn );

/**
* @name dmeanvar
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmeanvar}
*/
import dmeanvar from './../../strided/dmeanvar';
setReadOnly( ns, 'dmeanvar', dmeanvar );

/**
* @name dmeanvarpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmeanvarpn}
*/
import dmeanvarpn from './../../strided/dmeanvarpn';
setReadOnly( ns, 'dmeanvarpn', dmeanvarpn );

/**
* @name dmeanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmeanwd}
*/
import dmeanwd from './../../strided/dmeanwd';
setReadOnly( ns, 'dmeanwd', dmeanwd );

/**
* @name dmediansorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmediansorted}
*/
import dmediansorted from './../../strided/dmediansorted';
setReadOnly( ns, 'dmediansorted', dmediansorted );

/**
* @name dmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmidrange}
*/
import dmidrange from './../../strided/dmidrange';
setReadOnly( ns, 'dmidrange', dmidrange );

/**
* @name dmidrangeabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmidrangeabs}
*/
import dmidrangeabs from './../../strided/dmidrangeabs';
setReadOnly( ns, 'dmidrangeabs', dmidrangeabs );

/**
* @name dmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmin}
*/
import dmin from './../../strided/dmin';
setReadOnly( ns, 'dmin', dmin );

/**
* @name dminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dminabs}
*/
import dminabs from './../../strided/dminabs';
setReadOnly( ns, 'dminabs', dminabs );

/**
* @name dminsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dminsorted}
*/
import dminsorted from './../../strided/dminsorted';
setReadOnly( ns, 'dminsorted', dminsorted );

/**
* @name dmskmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmskmax}
*/
import dmskmax from './../../strided/dmskmax';
setReadOnly( ns, 'dmskmax', dmskmax );

/**
* @name dmskmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmskmaxabs}
*/
import dmskmaxabs from './../../strided/dmskmaxabs';
setReadOnly( ns, 'dmskmaxabs', dmskmaxabs );

/**
* @name dmskmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmskmidrange}
*/
import dmskmidrange from './../../strided/dmskmidrange';
setReadOnly( ns, 'dmskmidrange', dmskmidrange );

/**
* @name dmskmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmskmin}
*/
import dmskmin from './../../strided/dmskmin';
setReadOnly( ns, 'dmskmin', dmskmin );

/**
* @name dmskrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dmskrange}
*/
import dmskrange from './../../strided/dmskrange';
setReadOnly( ns, 'dmskrange', dmskrange );

/**
* @name dnanmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmax}
*/
import dnanmax from './../../strided/dnanmax';
setReadOnly( ns, 'dnanmax', dnanmax );

/**
* @name dnanmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmaxabs}
*/
import dnanmaxabs from './../../strided/dnanmaxabs';
setReadOnly( ns, 'dnanmaxabs', dnanmaxabs );

/**
* @name dnanmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmean}
*/
import dnanmean from './../../strided/dnanmean';
setReadOnly( ns, 'dnanmean', dnanmean );

/**
* @name dnanmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmeanors}
*/
import dnanmeanors from './../../strided/dnanmeanors';
setReadOnly( ns, 'dnanmeanors', dnanmeanors );

/**
* @name dnanmeanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmeanpn}
*/
import dnanmeanpn from './../../strided/dnanmeanpn';
setReadOnly( ns, 'dnanmeanpn', dnanmeanpn );

/**
* @name dnanmeanpw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmeanpw}
*/
import dnanmeanpw from './../../strided/dnanmeanpw';
setReadOnly( ns, 'dnanmeanpw', dnanmeanpw );

/**
* @name dnanmeanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmeanwd}
*/
import dnanmeanwd from './../../strided/dnanmeanwd';
setReadOnly( ns, 'dnanmeanwd', dnanmeanwd );

/**
* @name dnanmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmidrange}
*/
import dnanmidrange from './../../strided/dnanmidrange';
setReadOnly( ns, 'dnanmidrange', dnanmidrange );

/**
* @name dnanmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmin}
*/
import dnanmin from './../../strided/dnanmin';
setReadOnly( ns, 'dnanmin', dnanmin );

/**
* @name dnanminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanminabs}
*/
import dnanminabs from './../../strided/dnanminabs';
setReadOnly( ns, 'dnanminabs', dnanminabs );

/**
* @name dnanmskmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmskmax}
*/
import dnanmskmax from './../../strided/dnanmskmax';
setReadOnly( ns, 'dnanmskmax', dnanmskmax );

/**
* @name dnanmskmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmskmaxabs}
*/
import dnanmskmaxabs from './../../strided/dnanmskmaxabs';
setReadOnly( ns, 'dnanmskmaxabs', dnanmskmaxabs );

/**
* @name dnanmskmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmskmidrange}
*/
import dnanmskmidrange from './../../strided/dnanmskmidrange';
setReadOnly( ns, 'dnanmskmidrange', dnanmskmidrange );

/**
* @name dnanmskmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmskmin}
*/
import dnanmskmin from './../../strided/dnanmskmin';
setReadOnly( ns, 'dnanmskmin', dnanmskmin );

/**
* @name dnanmskminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmskminabs}
*/
import dnanmskminabs from './../../strided/dnanmskminabs';
setReadOnly( ns, 'dnanmskminabs', dnanmskminabs );

/**
* @name dnanmskrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanmskrange}
*/
import dnanmskrange from './../../strided/dnanmskrange';
setReadOnly( ns, 'dnanmskrange', dnanmskrange );

/**
* @name dnanrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanrange}
*/
import dnanrange from './../../strided/dnanrange';
setReadOnly( ns, 'dnanrange', dnanrange );

/**
* @name dnanrangeabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanrangeabs}
*/
import dnanrangeabs from './../../strided/dnanrangeabs';
setReadOnly( ns, 'dnanrangeabs', dnanrangeabs );

/**
* @name dnanstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanstdev}
*/
import dnanstdev from './../../strided/dnanstdev';
setReadOnly( ns, 'dnanstdev', dnanstdev );

/**
* @name dnanstdevch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanstdevch}
*/
import dnanstdevch from './../../strided/dnanstdevch';
setReadOnly( ns, 'dnanstdevch', dnanstdevch );

/**
* @name dnanstdevpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanstdevpn}
*/
import dnanstdevpn from './../../strided/dnanstdevpn';
setReadOnly( ns, 'dnanstdevpn', dnanstdevpn );

/**
* @name dnanstdevtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanstdevtk}
*/
import dnanstdevtk from './../../strided/dnanstdevtk';
setReadOnly( ns, 'dnanstdevtk', dnanstdevtk );

/**
* @name dnanstdevwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanstdevwd}
*/
import dnanstdevwd from './../../strided/dnanstdevwd';
setReadOnly( ns, 'dnanstdevwd', dnanstdevwd );

/**
* @name dnanstdevyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanstdevyc}
*/
import dnanstdevyc from './../../strided/dnanstdevyc';
setReadOnly( ns, 'dnanstdevyc', dnanstdevyc );

/**
* @name dnanvariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanvariance}
*/
import dnanvariance from './../../strided/dnanvariance';
setReadOnly( ns, 'dnanvariance', dnanvariance );

/**
* @name dnanvariancech
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanvariancech}
*/
import dnanvariancech from './../../strided/dnanvariancech';
setReadOnly( ns, 'dnanvariancech', dnanvariancech );

/**
* @name dnanvariancepn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanvariancepn}
*/
import dnanvariancepn from './../../strided/dnanvariancepn';
setReadOnly( ns, 'dnanvariancepn', dnanvariancepn );

/**
* @name dnanvariancetk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanvariancetk}
*/
import dnanvariancetk from './../../strided/dnanvariancetk';
setReadOnly( ns, 'dnanvariancetk', dnanvariancetk );

/**
* @name dnanvariancewd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanvariancewd}
*/
import dnanvariancewd from './../../strided/dnanvariancewd';
setReadOnly( ns, 'dnanvariancewd', dnanvariancewd );

/**
* @name dnanvarianceyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dnanvarianceyc}
*/
import dnanvarianceyc from './../../strided/dnanvarianceyc';
setReadOnly( ns, 'dnanvarianceyc', dnanvarianceyc );

/**
* @name dpcorr
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dpcorr}
*/
import dpcorr from './../../strided/dpcorr';
setReadOnly( ns, 'dpcorr', dpcorr );

/**
* @name dpcorrwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dpcorrwd}
*/
import dpcorrwd from './../../strided/dpcorrwd';
setReadOnly( ns, 'dpcorrwd', dpcorrwd );

/**
* @name drange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/drange}
*/
import drange from './../../strided/drange';
setReadOnly( ns, 'drange', drange );

/**
* @name drangeabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/drangeabs}
*/
import drangeabs from './../../strided/drangeabs';
setReadOnly( ns, 'drangeabs', drangeabs );

/**
* @name dsem
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsem}
*/
import dsem from './../../strided/dsem';
setReadOnly( ns, 'dsem', dsem );

/**
* @name dsemch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsemch}
*/
import dsemch from './../../strided/dsemch';
setReadOnly( ns, 'dsemch', dsemch );

/**
* @name dsempn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsempn}
*/
import dsempn from './../../strided/dsempn';
setReadOnly( ns, 'dsempn', dsempn );

/**
* @name dsemtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsemtk}
*/
import dsemtk from './../../strided/dsemtk';
setReadOnly( ns, 'dsemtk', dsemtk );

/**
* @name dsemwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsemwd}
*/
import dsemwd from './../../strided/dsemwd';
setReadOnly( ns, 'dsemwd', dsemwd );

/**
* @name dsemyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsemyc}
*/
import dsemyc from './../../strided/dsemyc';
setReadOnly( ns, 'dsemyc', dsemyc );

/**
* @name dsmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsmean}
*/
import dsmean from './../../strided/dsmean';
setReadOnly( ns, 'dsmean', dsmean );

/**
* @name dsmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsmeanors}
*/
import dsmeanors from './../../strided/dsmeanors';
setReadOnly( ns, 'dsmeanors', dsmeanors );

/**
* @name dsmeanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsmeanpn}
*/
import dsmeanpn from './../../strided/dsmeanpn';
setReadOnly( ns, 'dsmeanpn', dsmeanpn );

/**
* @name dsmeanpw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsmeanpw}
*/
import dsmeanpw from './../../strided/dsmeanpw';
setReadOnly( ns, 'dsmeanpw', dsmeanpw );

/**
* @name dsmeanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsmeanwd}
*/
import dsmeanwd from './../../strided/dsmeanwd';
setReadOnly( ns, 'dsmeanwd', dsmeanwd );

/**
* @name dsnanmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsnanmean}
*/
import dsnanmean from './../../strided/dsnanmean';
setReadOnly( ns, 'dsnanmean', dsnanmean );

/**
* @name dsnanmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsnanmeanors}
*/
import dsnanmeanors from './../../strided/dsnanmeanors';
setReadOnly( ns, 'dsnanmeanors', dsnanmeanors );

/**
* @name dsnanmeanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsnanmeanpn}
*/
import dsnanmeanpn from './../../strided/dsnanmeanpn';
setReadOnly( ns, 'dsnanmeanpn', dsnanmeanpn );

/**
* @name dsnanmeanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsnanmeanwd}
*/
import dsnanmeanwd from './../../strided/dsnanmeanwd';
setReadOnly( ns, 'dsnanmeanwd', dsnanmeanwd );

/**
* @name dstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dstdev}
*/
import dstdev from './../../strided/dstdev';
setReadOnly( ns, 'dstdev', dstdev );

/**
* @name dstdevch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dstdevch}
*/
import dstdevch from './../../strided/dstdevch';
setReadOnly( ns, 'dstdevch', dstdevch );

/**
* @name dstdevpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dstdevpn}
*/
import dstdevpn from './../../strided/dstdevpn';
setReadOnly( ns, 'dstdevpn', dstdevpn );

/**
* @name dstdevtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dstdevtk}
*/
import dstdevtk from './../../strided/dstdevtk';
setReadOnly( ns, 'dstdevtk', dstdevtk );

/**
* @name dstdevwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dstdevwd}
*/
import dstdevwd from './../../strided/dstdevwd';
setReadOnly( ns, 'dstdevwd', dstdevwd );

/**
* @name dstdevyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dstdevyc}
*/
import dstdevyc from './../../strided/dstdevyc';
setReadOnly( ns, 'dstdevyc', dstdevyc );

/**
* @name dsvariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsvariance}
*/
import dsvariance from './../../strided/dsvariance';
setReadOnly( ns, 'dsvariance', dsvariance );

/**
* @name dsvariancepn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dsvariancepn}
*/
import dsvariancepn from './../../strided/dsvariancepn';
setReadOnly( ns, 'dsvariancepn', dsvariancepn );

/**
* @name dvariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dvariance}
*/
import dvariance from './../../strided/dvariance';
setReadOnly( ns, 'dvariance', dvariance );

/**
* @name dvariancech
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dvariancech}
*/
import dvariancech from './../../strided/dvariancech';
setReadOnly( ns, 'dvariancech', dvariancech );

/**
* @name dvariancepn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dvariancepn}
*/
import dvariancepn from './../../strided/dvariancepn';
setReadOnly( ns, 'dvariancepn', dvariancepn );

/**
* @name dvariancetk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dvariancetk}
*/
import dvariancetk from './../../strided/dvariancetk';
setReadOnly( ns, 'dvariancetk', dvariancetk );

/**
* @name dvariancewd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dvariancewd}
*/
import dvariancewd from './../../strided/dvariancewd';
setReadOnly( ns, 'dvariancewd', dvariancewd );

/**
* @name dvarianceyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dvarianceyc}
*/
import dvarianceyc from './../../strided/dvarianceyc';
setReadOnly( ns, 'dvarianceyc', dvarianceyc );

/**
* @name dvarm
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dvarm}
*/
import dvarm from './../../strided/dvarm';
setReadOnly( ns, 'dvarm', dvarm );

/**
* @name dvarmpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dvarmpn}
*/
import dvarmpn from './../../strided/dvarmpn';
setReadOnly( ns, 'dvarmpn', dvarmpn );

/**
* @name dvarmtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dvarmtk}
*/
import dvarmtk from './../../strided/dvarmtk';
setReadOnly( ns, 'dvarmtk', dvarmtk );

/**
* @name dztest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dztest}
*/
import dztest from './../../strided/dztest';
setReadOnly( ns, 'dztest', dztest );

/**
* @name dztest2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/dztest2}
*/
import dztest2 from './../../strided/dztest2';
setReadOnly( ns, 'dztest2', dztest2 );

/**
* @name max
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/max}
*/
import max from './../../strided/max';
setReadOnly( ns, 'max', max );

/**
* @name maxBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/max-by}
*/
import maxBy from './../../strided/max-by';
setReadOnly( ns, 'maxBy', maxBy );

/**
* @name maxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/maxabs}
*/
import maxabs from './../../strided/maxabs';
setReadOnly( ns, 'maxabs', maxabs );

/**
* @name maxsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/maxsorted}
*/
import maxsorted from './../../strided/maxsorted';
setReadOnly( ns, 'maxsorted', maxsorted );

/**
* @name mean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/mean}
*/
import mean from './../../strided/mean';
setReadOnly( ns, 'mean', mean );

/**
* @name meankbn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/meankbn}
*/
import meankbn from './../../strided/meankbn';
setReadOnly( ns, 'meankbn', meankbn );

/**
* @name meankbn2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/meankbn2}
*/
import meankbn2 from './../../strided/meankbn2';
setReadOnly( ns, 'meankbn2', meankbn2 );

/**
* @name meanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/meanors}
*/
import meanors from './../../strided/meanors';
setReadOnly( ns, 'meanors', meanors );

/**
* @name meanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/meanpn}
*/
import meanpn from './../../strided/meanpn';
setReadOnly( ns, 'meanpn', meanpn );

/**
* @name meanpw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/meanpw}
*/
import meanpw from './../../strided/meanpw';
setReadOnly( ns, 'meanpw', meanpw );

/**
* @name meanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/meanwd}
*/
import meanwd from './../../strided/meanwd';
setReadOnly( ns, 'meanwd', meanwd );

/**
* @name mediansorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/mediansorted}
*/
import mediansorted from './../../strided/mediansorted';
setReadOnly( ns, 'mediansorted', mediansorted );

/**
* @name midrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/midrange}
*/
import midrange from './../../strided/midrange';
setReadOnly( ns, 'midrange', midrange );

/**
* @name midrangeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/midrange-by}
*/
import midrangeBy from './../../strided/midrange-by';
setReadOnly( ns, 'midrangeBy', midrangeBy );

/**
* @name midrangeabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/midrangeabs}
*/
import midrangeabs from './../../strided/midrangeabs';
setReadOnly( ns, 'midrangeabs', midrangeabs );

/**
* @name min
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/min}
*/
import min from './../../strided/min';
setReadOnly( ns, 'min', min );

/**
* @name minBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/min-by}
*/
import minBy from './../../strided/min-by';
setReadOnly( ns, 'minBy', minBy );

/**
* @name minabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/minabs}
*/
import minabs from './../../strided/minabs';
setReadOnly( ns, 'minabs', minabs );

/**
* @name minsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/minsorted}
*/
import minsorted from './../../strided/minsorted';
setReadOnly( ns, 'minsorted', minsorted );

/**
* @name mskmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/mskmax}
*/
import mskmax from './../../strided/mskmax';
setReadOnly( ns, 'mskmax', mskmax );

/**
* @name mskmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/mskmaxabs}
*/
import mskmaxabs from './../../strided/mskmaxabs';
setReadOnly( ns, 'mskmaxabs', mskmaxabs );

/**
* @name mskmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/mskmidrange}
*/
import mskmidrange from './../../strided/mskmidrange';
setReadOnly( ns, 'mskmidrange', mskmidrange );

/**
* @name mskmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/mskmin}
*/
import mskmin from './../../strided/mskmin';
setReadOnly( ns, 'mskmin', mskmin );

/**
* @name mskminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/mskminabs}
*/
import mskminabs from './../../strided/mskminabs';
setReadOnly( ns, 'mskminabs', mskminabs );

/**
* @name mskrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/mskrange}
*/
import mskrange from './../../strided/mskrange';
setReadOnly( ns, 'mskrange', mskrange );

/**
* @name nanmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmax}
*/
import nanmax from './../../strided/nanmax';
setReadOnly( ns, 'nanmax', nanmax );

/**
* @name nanmaxBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmax-by}
*/
import nanmaxBy from './../../strided/nanmax-by';
setReadOnly( ns, 'nanmaxBy', nanmaxBy );

/**
* @name nanmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmaxabs}
*/
import nanmaxabs from './../../strided/nanmaxabs';
setReadOnly( ns, 'nanmaxabs', nanmaxabs );

/**
* @name nanmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmean}
*/
import nanmean from './../../strided/nanmean';
setReadOnly( ns, 'nanmean', nanmean );

/**
* @name nanmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmeanors}
*/
import nanmeanors from './../../strided/nanmeanors';
setReadOnly( ns, 'nanmeanors', nanmeanors );

/**
* @name nanmeanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmeanpn}
*/
import nanmeanpn from './../../strided/nanmeanpn';
setReadOnly( ns, 'nanmeanpn', nanmeanpn );

/**
* @name nanmeanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmeanwd}
*/
import nanmeanwd from './../../strided/nanmeanwd';
setReadOnly( ns, 'nanmeanwd', nanmeanwd );

/**
* @name nanmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmidrange}
*/
import nanmidrange from './../../strided/nanmidrange';
setReadOnly( ns, 'nanmidrange', nanmidrange );

/**
* @name nanmidrangeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmidrange-by}
*/
import nanmidrangeBy from './../../strided/nanmidrange-by';
setReadOnly( ns, 'nanmidrangeBy', nanmidrangeBy );

/**
* @name nanmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmin}
*/
import nanmin from './../../strided/nanmin';
setReadOnly( ns, 'nanmin', nanmin );

/**
* @name nanminBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmin-by}
*/
import nanminBy from './../../strided/nanmin-by';
setReadOnly( ns, 'nanminBy', nanminBy );

/**
* @name nanminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanminabs}
*/
import nanminabs from './../../strided/nanminabs';
setReadOnly( ns, 'nanminabs', nanminabs );

/**
* @name nanmskmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmskmax}
*/
import nanmskmax from './../../strided/nanmskmax';
setReadOnly( ns, 'nanmskmax', nanmskmax );

/**
* @name nanmskmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmskmidrange}
*/
import nanmskmidrange from './../../strided/nanmskmidrange';
setReadOnly( ns, 'nanmskmidrange', nanmskmidrange );

/**
* @name nanmskmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmskmin}
*/
import nanmskmin from './../../strided/nanmskmin';
setReadOnly( ns, 'nanmskmin', nanmskmin );

/**
* @name nanmskrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanmskrange}
*/
import nanmskrange from './../../strided/nanmskrange';
setReadOnly( ns, 'nanmskrange', nanmskrange );

/**
* @name nanrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanrange}
*/
import nanrange from './../../strided/nanrange';
setReadOnly( ns, 'nanrange', nanrange );

/**
* @name nanrangeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanrange-by}
*/
import nanrangeBy from './../../strided/nanrange-by';
setReadOnly( ns, 'nanrangeBy', nanrangeBy );

/**
* @name nanrangeabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanrangeabs}
*/
import nanrangeabs from './../../strided/nanrangeabs';
setReadOnly( ns, 'nanrangeabs', nanrangeabs );

/**
* @name nanstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanstdev}
*/
import nanstdev from './../../strided/nanstdev';
setReadOnly( ns, 'nanstdev', nanstdev );

/**
* @name nanstdevch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanstdevch}
*/
import nanstdevch from './../../strided/nanstdevch';
setReadOnly( ns, 'nanstdevch', nanstdevch );

/**
* @name nanstdevpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanstdevpn}
*/
import nanstdevpn from './../../strided/nanstdevpn';
setReadOnly( ns, 'nanstdevpn', nanstdevpn );

/**
* @name nanstdevtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanstdevtk}
*/
import nanstdevtk from './../../strided/nanstdevtk';
setReadOnly( ns, 'nanstdevtk', nanstdevtk );

/**
* @name nanstdevwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanstdevwd}
*/
import nanstdevwd from './../../strided/nanstdevwd';
setReadOnly( ns, 'nanstdevwd', nanstdevwd );

/**
* @name nanstdevyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanstdevyc}
*/
import nanstdevyc from './../../strided/nanstdevyc';
setReadOnly( ns, 'nanstdevyc', nanstdevyc );

/**
* @name nanvariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanvariance}
*/
import nanvariance from './../../strided/nanvariance';
setReadOnly( ns, 'nanvariance', nanvariance );

/**
* @name nanvariancech
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanvariancech}
*/
import nanvariancech from './../../strided/nanvariancech';
setReadOnly( ns, 'nanvariancech', nanvariancech );

/**
* @name nanvariancepn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanvariancepn}
*/
import nanvariancepn from './../../strided/nanvariancepn';
setReadOnly( ns, 'nanvariancepn', nanvariancepn );

/**
* @name nanvariancetk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanvariancetk}
*/
import nanvariancetk from './../../strided/nanvariancetk';
setReadOnly( ns, 'nanvariancetk', nanvariancetk );

/**
* @name nanvariancewd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanvariancewd}
*/
import nanvariancewd from './../../strided/nanvariancewd';
setReadOnly( ns, 'nanvariancewd', nanvariancewd );

/**
* @name nanvarianceyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/nanvarianceyc}
*/
import nanvarianceyc from './../../strided/nanvarianceyc';
setReadOnly( ns, 'nanvarianceyc', nanvarianceyc );

/**
* @name range
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/range}
*/
import range from './../../strided/range';
setReadOnly( ns, 'range', range );

/**
* @name rangeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/range-by}
*/
import rangeBy from './../../strided/range-by';
setReadOnly( ns, 'rangeBy', rangeBy );

/**
* @name rangeabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/rangeabs}
*/
import rangeabs from './../../strided/rangeabs';
setReadOnly( ns, 'rangeabs', rangeabs );

/**
* @name scovarmtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/scovarmtk}
*/
import scovarmtk from './../../strided/scovarmtk';
setReadOnly( ns, 'scovarmtk', scovarmtk );

/**
* @name scumax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/scumax}
*/
import scumax from './../../strided/scumax';
setReadOnly( ns, 'scumax', scumax );

/**
* @name scumaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/scumaxabs}
*/
import scumaxabs from './../../strided/scumaxabs';
setReadOnly( ns, 'scumaxabs', scumaxabs );

/**
* @name scumin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/scumin}
*/
import scumin from './../../strided/scumin';
setReadOnly( ns, 'scumin', scumin );

/**
* @name scuminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/scuminabs}
*/
import scuminabs from './../../strided/scuminabs';
setReadOnly( ns, 'scuminabs', scuminabs );

/**
* @name sdsmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/sdsmean}
*/
import sdsmean from './../../strided/sdsmean';
setReadOnly( ns, 'sdsmean', sdsmean );

/**
* @name sdsmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/sdsmeanors}
*/
import sdsmeanors from './../../strided/sdsmeanors';
setReadOnly( ns, 'sdsmeanors', sdsmeanors );

/**
* @name sdsnanmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/sdsnanmeanors}
*/
import sdsnanmeanors from './../../strided/sdsnanmeanors';
setReadOnly( ns, 'sdsnanmeanors', sdsnanmeanors );

/**
* @name smax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smax}
*/
import smax from './../../strided/smax';
setReadOnly( ns, 'smax', smax );

/**
* @name smaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smaxabs}
*/
import smaxabs from './../../strided/smaxabs';
setReadOnly( ns, 'smaxabs', smaxabs );

/**
* @name smaxabssorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smaxabssorted}
*/
import smaxabssorted from './../../strided/smaxabssorted';
setReadOnly( ns, 'smaxabssorted', smaxabssorted );

/**
* @name smaxsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smaxsorted}
*/
import smaxsorted from './../../strided/smaxsorted';
setReadOnly( ns, 'smaxsorted', smaxsorted );

/**
* @name smean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smean}
*/
import smean from './../../strided/smean';
setReadOnly( ns, 'smean', smean );

/**
* @name smeankbn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smeankbn}
*/
import smeankbn from './../../strided/smeankbn';
setReadOnly( ns, 'smeankbn', smeankbn );

/**
* @name smeankbn2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smeankbn2}
*/
import smeankbn2 from './../../strided/smeankbn2';
setReadOnly( ns, 'smeankbn2', smeankbn2 );

/**
* @name smeanli
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smeanli}
*/
import smeanli from './../../strided/smeanli';
setReadOnly( ns, 'smeanli', smeanli );

/**
* @name smeanlipw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smeanlipw}
*/
import smeanlipw from './../../strided/smeanlipw';
setReadOnly( ns, 'smeanlipw', smeanlipw );

/**
* @name smeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smeanors}
*/
import smeanors from './../../strided/smeanors';
setReadOnly( ns, 'smeanors', smeanors );

/**
* @name smeanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smeanpn}
*/
import smeanpn from './../../strided/smeanpn';
setReadOnly( ns, 'smeanpn', smeanpn );

/**
* @name smeanpw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smeanpw}
*/
import smeanpw from './../../strided/smeanpw';
setReadOnly( ns, 'smeanpw', smeanpw );

/**
* @name smeanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smeanwd}
*/
import smeanwd from './../../strided/smeanwd';
setReadOnly( ns, 'smeanwd', smeanwd );

/**
* @name smediansorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smediansorted}
*/
import smediansorted from './../../strided/smediansorted';
setReadOnly( ns, 'smediansorted', smediansorted );

/**
* @name smidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smidrange}
*/
import smidrange from './../../strided/smidrange';
setReadOnly( ns, 'smidrange', smidrange );

/**
* @name smin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smin}
*/
import smin from './../../strided/smin';
setReadOnly( ns, 'smin', smin );

/**
* @name sminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/sminabs}
*/
import sminabs from './../../strided/sminabs';
setReadOnly( ns, 'sminabs', sminabs );

/**
* @name sminsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/sminsorted}
*/
import sminsorted from './../../strided/sminsorted';
setReadOnly( ns, 'sminsorted', sminsorted );

/**
* @name smskmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smskmax}
*/
import smskmax from './../../strided/smskmax';
setReadOnly( ns, 'smskmax', smskmax );

/**
* @name smskmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smskmaxabs}
*/
import smskmaxabs from './../../strided/smskmaxabs';
setReadOnly( ns, 'smskmaxabs', smskmaxabs );

/**
* @name smskmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smskmidrange}
*/
import smskmidrange from './../../strided/smskmidrange';
setReadOnly( ns, 'smskmidrange', smskmidrange );

/**
* @name smskmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smskmin}
*/
import smskmin from './../../strided/smskmin';
setReadOnly( ns, 'smskmin', smskmin );

/**
* @name smskrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/smskrange}
*/
import smskrange from './../../strided/smskrange';
setReadOnly( ns, 'smskrange', smskrange );

/**
* @name snanmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanmax}
*/
import snanmax from './../../strided/snanmax';
setReadOnly( ns, 'snanmax', snanmax );

/**
* @name snanmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanmaxabs}
*/
import snanmaxabs from './../../strided/snanmaxabs';
setReadOnly( ns, 'snanmaxabs', snanmaxabs );

/**
* @name snanmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanmean}
*/
import snanmean from './../../strided/snanmean';
setReadOnly( ns, 'snanmean', snanmean );

/**
* @name snanmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanmeanors}
*/
import snanmeanors from './../../strided/snanmeanors';
setReadOnly( ns, 'snanmeanors', snanmeanors );

/**
* @name snanmeanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanmeanpn}
*/
import snanmeanpn from './../../strided/snanmeanpn';
setReadOnly( ns, 'snanmeanpn', snanmeanpn );

/**
* @name snanmeanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanmeanwd}
*/
import snanmeanwd from './../../strided/snanmeanwd';
setReadOnly( ns, 'snanmeanwd', snanmeanwd );

/**
* @name snanmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanmidrange}
*/
import snanmidrange from './../../strided/snanmidrange';
setReadOnly( ns, 'snanmidrange', snanmidrange );

/**
* @name snanmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanmin}
*/
import snanmin from './../../strided/snanmin';
setReadOnly( ns, 'snanmin', snanmin );

/**
* @name snanminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanminabs}
*/
import snanminabs from './../../strided/snanminabs';
setReadOnly( ns, 'snanminabs', snanminabs );

/**
* @name snanmskmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanmskmax}
*/
import snanmskmax from './../../strided/snanmskmax';
setReadOnly( ns, 'snanmskmax', snanmskmax );

/**
* @name snanmskmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanmskmaxabs}
*/
import snanmskmaxabs from './../../strided/snanmskmaxabs';
setReadOnly( ns, 'snanmskmaxabs', snanmskmaxabs );

/**
* @name snanmskmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanmskmidrange}
*/
import snanmskmidrange from './../../strided/snanmskmidrange';
setReadOnly( ns, 'snanmskmidrange', snanmskmidrange );

/**
* @name snanmskmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanmskmin}
*/
import snanmskmin from './../../strided/snanmskmin';
setReadOnly( ns, 'snanmskmin', snanmskmin );

/**
* @name snanmskminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanmskminabs}
*/
import snanmskminabs from './../../strided/snanmskminabs';
setReadOnly( ns, 'snanmskminabs', snanmskminabs );

/**
* @name snanmskrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanmskrange}
*/
import snanmskrange from './../../strided/snanmskrange';
setReadOnly( ns, 'snanmskrange', snanmskrange );

/**
* @name snanrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/snanrange}
*/
import snanrange from './../../strided/snanrange';
setReadOnly( ns, 'snanrange', snanrange );

/**
* @name srange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/srange}
*/
import srange from './../../strided/srange';
setReadOnly( ns, 'srange', srange );

/**
* @name srangeabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/srangeabs}
*/
import srangeabs from './../../strided/srangeabs';
setReadOnly( ns, 'srangeabs', srangeabs );

/**
* @name sstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/sstdev}
*/
import sstdev from './../../strided/sstdev';
setReadOnly( ns, 'sstdev', sstdev );

/**
* @name sstdevch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/sstdevch}
*/
import sstdevch from './../../strided/sstdevch';
setReadOnly( ns, 'sstdevch', sstdevch );

/**
* @name sstdevpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/sstdevpn}
*/
import sstdevpn from './../../strided/sstdevpn';
setReadOnly( ns, 'sstdevpn', sstdevpn );

/**
* @name sstdevtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/sstdevtk}
*/
import sstdevtk from './../../strided/sstdevtk';
setReadOnly( ns, 'sstdevtk', sstdevtk );

/**
* @name sstdevwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/sstdevwd}
*/
import sstdevwd from './../../strided/sstdevwd';
setReadOnly( ns, 'sstdevwd', sstdevwd );

/**
* @name sstdevyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/sstdevyc}
*/
import sstdevyc from './../../strided/sstdevyc';
setReadOnly( ns, 'sstdevyc', sstdevyc );

/**
* @name stdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/stdev}
*/
import stdev from './../../strided/stdev';
setReadOnly( ns, 'stdev', stdev );

/**
* @name stdevch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/stdevch}
*/
import stdevch from './../../strided/stdevch';
setReadOnly( ns, 'stdevch', stdevch );

/**
* @name stdevpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/stdevpn}
*/
import stdevpn from './../../strided/stdevpn';
setReadOnly( ns, 'stdevpn', stdevpn );

/**
* @name stdevtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/stdevtk}
*/
import stdevtk from './../../strided/stdevtk';
setReadOnly( ns, 'stdevtk', stdevtk );

/**
* @name stdevwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/stdevwd}
*/
import stdevwd from './../../strided/stdevwd';
setReadOnly( ns, 'stdevwd', stdevwd );

/**
* @name stdevyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/stdevyc}
*/
import stdevyc from './../../strided/stdevyc';
setReadOnly( ns, 'stdevyc', stdevyc );

/**
* @name svariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/svariance}
*/
import svariance from './../../strided/svariance';
setReadOnly( ns, 'svariance', svariance );

/**
* @name svariancech
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/svariancech}
*/
import svariancech from './../../strided/svariancech';
setReadOnly( ns, 'svariancech', svariancech );

/**
* @name svariancepn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/svariancepn}
*/
import svariancepn from './../../strided/svariancepn';
setReadOnly( ns, 'svariancepn', svariancepn );

/**
* @name svariancetk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/svariancetk}
*/
import svariancetk from './../../strided/svariancetk';
setReadOnly( ns, 'svariancetk', svariancetk );

/**
* @name svariancewd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/svariancewd}
*/
import svariancewd from './../../strided/svariancewd';
setReadOnly( ns, 'svariancewd', svariancewd );

/**
* @name svarianceyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/svarianceyc}
*/
import svarianceyc from './../../strided/svarianceyc';
setReadOnly( ns, 'svarianceyc', svarianceyc );

/**
* @name sztest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/sztest}
*/
import sztest from './../../strided/sztest';
setReadOnly( ns, 'sztest', sztest );

/**
* @name sztest2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/sztest2}
*/
import sztest2 from './../../strided/sztest2';
setReadOnly( ns, 'sztest2', sztest2 );

/**
* @name variance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/variance}
*/
import variance from './../../strided/variance';
setReadOnly( ns, 'variance', variance );

/**
* @name variancech
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/variancech}
*/
import variancech from './../../strided/variancech';
setReadOnly( ns, 'variancech', variancech );

/**
* @name variancepn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/variancepn}
*/
import variancepn from './../../strided/variancepn';
setReadOnly( ns, 'variancepn', variancepn );

/**
* @name variancetk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/variancetk}
*/
import variancetk from './../../strided/variancetk';
setReadOnly( ns, 'variancetk', variancetk );

/**
* @name variancewd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/variancewd}
*/
import variancewd from './../../strided/variancewd';
setReadOnly( ns, 'variancewd', variancewd );

/**
* @name varianceyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/varianceyc}
*/
import varianceyc from './../../strided/varianceyc';
setReadOnly( ns, 'varianceyc', varianceyc );

/**
* @name ztest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/ztest}
*/
import ztest from './../../strided/ztest';
setReadOnly( ns, 'ztest', ztest );

/**
* @name ztest2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/strided/ztest2}
*/
import ztest2 from './../../strided/ztest2';
setReadOnly( ns, 'ztest2', ztest2 );


// EXPORTS //

export default ns;
